/**
 * Find the first dom node that matches the given `predicate` by walking up the
 * DOM. This method can return the `start` node if it matches.
 *
 * @param start The node to start the search from.
 * @param predicate A predicate function receiving the current DOM node.
 * @param stopParent The node to stop the search at. If this is reached before a
 *     node is found, `null` is returned. Defaults to the `document`.
 *
 * @return The first matching node, or `null` if none is found before
 *     `stopParent` is reached.
 */
export function getFirstMatchingParent(
  start: HTMLElement,
  predicate: (ele: HTMLElement) => boolean,
  stopParent: HTMLElement = (document as unknown) as HTMLElement
): HTMLElement | null {
  for (
    let el = start;
    el !== stopParent && el;
    el = el.parentNode as HTMLElement
  ) {
    if (predicate(el)) {
      return el;
    }
  }

  return null;
}

export function createElement(
  tagName: string,
  attributes: object = {},
  children: Array<HTMLElement> = []
) {
  const ele = document.createElement(tagName);

  for (let a in attributes) {
    if (a === '__text') {
      ele.innerText = attributes[a];
    } else {
      ele.setAttribute(a, attributes[a]);
    }
  }

  children.forEach(c => ele.appendChild(c));

  return ele;
}

export function prependChild(parent: HTMLElement, child: HTMLElement) {
  if (parent.firstChild) {
    parent.insertBefore(child, parent.firstChild);
  } else {
    parent.appendChild(child);
  }
}
